import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Wrench, ArrowLeft } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { providerAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * Dedicated service provider signup form. Mirrors the Driver / Restaurant
 * pages so a user routed in from /add-role gets a consistent dedicated
 * page rather than the generic /signup carousel.
 *
 * Service categories selection is a checkbox list — most providers offer
 * more than one (e.g. cleaning + linen turnover). Compliance documents
 * (PLI, DBS, Right to Work) are collected later from the dashboard.
 */
const SERVICE_TYPES = [
  { id: 'cleaning',    label: 'Cleaning' },
  { id: 'linen',       label: 'Linen / Laundry' },
  { id: 'handyman',    label: 'Handyman / Repairs' },
  { id: 'maintenance', label: 'Maintenance' },
  { id: 'inspection',  label: 'Property Inspection' },
  { id: 'gardening',   label: 'Gardening' },
];

const ServiceProviderSignupPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { login, user: authedUser, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const isAddRoleMode = !!authedUser;

  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    serviceTypes: [],
  });

  useEffect(() => {
    const refCode = new URLSearchParams(location.search).get('ref');
    if (refCode) localStorage.setItem('bc_ref_code', refCode.trim().toUpperCase());
  }, [location.search]);

  useEffect(() => {
    if (isAddRoleMode && authedUser?.name && !form.name) {
      setForm(prev => ({ ...prev, name: authedUser.name }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddRoleMode]);

  const toggleService = (id) => {
    setForm(p => ({
      ...p,
      serviceTypes: p.serviceTypes.includes(id)
        ? p.serviceTypes.filter(s => s !== id)
        : [...p.serviceTypes, id],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isAddRoleMode) {
      if (form.password !== form.confirmPassword) {
        toast({ title: 'Error', description: 'Passwords do not match', variant: 'destructive' });
        return;
      }
      if (!form.name || !form.email || !form.password || !form.phone) {
        toast({ title: 'Error', description: 'Please fill in all required fields', variant: 'destructive' });
        return;
      }
    } else if (!form.name || !form.phone) {
      toast({ title: 'Error', description: 'Please fill in all required fields', variant: 'destructive' });
      return;
    }

    if (form.serviceTypes.length === 0) {
      toast({ title: 'Pick at least one service', description: 'Select the services you offer.', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      await providerAPI.register({
        name: form.name,
        email: isAddRoleMode ? authedUser.email : form.email,
        password: isAddRoleMode ? 'addrole-placeholder-ignored' : form.password,
        phone: form.phone,
        serviceTypes: form.serviceTypes,
        referredBy: localStorage.getItem('bc_ref_code') || undefined,
      });

      if (isAddRoleMode) {
        toast({ title: 'Service Provider role added', description: 'Welcome to your provider dashboard.' });
        try { await refreshUser?.(); } catch (_) {}
      } else {
        toast({ title: 'Success', description: 'Provider account created. Logging you in…' });
        await login(form.email, form.password);
      }
      navigate('/provider/dashboard');
    } catch (err) {
      toast({
        title: 'Registration failed',
        description: err.response?.data?.detail || 'Could not register service provider',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F2E9] flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <Button variant="ghost" size="icon" className="absolute left-4 top-4" onClick={() => navigate(-1)}>
            <ArrowLeft size={20} />
          </Button>
          <div className="mx-auto mb-4 p-3 bg-purple-600 rounded-full w-fit">
            <Wrench size={32} className="text-white" />
          </div>
          <CardTitle className="text-2xl text-[#1F3A68]">
            {isAddRoleMode ? 'Add Service Provider role' : 'Service Provider Signup'}
          </CardTitle>
          <CardDescription>Offer home services to hosts and guests on Ariv</CardDescription>
        </CardHeader>
        <CardContent>
          {isAddRoleMode && (
            <div className="mb-4 p-3 bg-[#1F3A68]/5 border border-[#1F3A68]/20 rounded-lg text-sm text-[#1F3A68]">
              <p className="font-semibold">Adding Service Provider role to your existing account</p>
              <p className="text-xs text-[#1F3A68]/80 mt-0.5 break-all">Linked to {authedUser?.email}.</p>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Full Name *</Label>
              <Input
                value={form.name}
                onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                placeholder="Jane Smith"
              />
            </div>

            {isAddRoleMode ? (
              <div>
                <Label>Phone *</Label>
                <Input
                  value={form.phone}
                  onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                  placeholder="+44 123 456 7890"
                />
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                      placeholder="you@example.com"
                    />
                  </div>
                  <div>
                    <Label>Phone *</Label>
                    <Input
                      value={form.phone}
                      onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                      placeholder="+44 123 456 7890"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Password *</Label>
                    <Input
                      type="password"
                      value={form.password}
                      onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                      placeholder="••••••••"
                    />
                  </div>
                  <div>
                    <Label>Confirm Password *</Label>
                    <Input
                      type="password"
                      value={form.confirmPassword}
                      onChange={e => setForm(p => ({ ...p, confirmPassword: e.target.value }))}
                      placeholder="••••••••"
                    />
                  </div>
                </div>
              </>
            )}

            <div>
              <Label>Services you offer *</Label>
              <p className="text-xs text-gray-500 mb-2">Pick all that apply. You can refine these later in your dashboard.</p>
              <div className="grid grid-cols-2 gap-2">
                {SERVICE_TYPES.map(s => {
                  const active = form.serviceTypes.includes(s.id);
                  return (
                    <button
                      type="button"
                      key={s.id}
                      onClick={() => toggleService(s.id)}
                      className={`text-left text-sm px-3 py-2 rounded-lg border transition-colors ${
                        active
                          ? 'bg-purple-600 text-white border-purple-700'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-purple-400'
                      }`}
                    >
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
              You'll be asked to upload your Public Liability Insurance, DBS check, and Right to Work documents from your
              dashboard before you can accept work. These take a few minutes to set up and a working day to verify.
            </div>

            <div className="flex items-start gap-2">
              <input type="checkbox" required className="w-4 h-4 rounded border-gray-300 mt-0.5" />
              <span className="text-xs text-gray-600">
                I agree to the{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-[#1F3A68] underline">Terms &amp; Conditions</a>
                {' '}and{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#1F3A68] underline">Privacy Policy</a>.
              </span>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white h-12 text-base"
            >
              {loading ? (isAddRoleMode ? 'Adding role…' : 'Creating account…') : (isAddRoleMode ? 'Add Service Provider role' : 'Create Provider Account')}
            </Button>

            {!isAddRoleMode && (
              <p className="text-xs text-center text-gray-600">
                Already have an account?{' '}
                <Link to="/login" className="text-[#1F3A68] font-semibold underline">Log in</Link>
              </p>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ServiceProviderSignupPage;
